import { useEffect, useState } from 'react';
import { FaBell, FaTimes } from 'react-icons/fa';
import { registerPushNotifications } from '../services/pushNotificationService';
import './PushNotificationPrompt.css';

const DISMISS_KEY = 'push_prompt_dismissed_at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const shouldShowPrompt = () => {
  if (typeof window === 'undefined' || !('Notification' in window)) return false;
  if (!localStorage.getItem('token')) return false;
  if (Notification.permission !== 'default') return false;

  const dismissedAt = Number(sessionStorage.getItem(DISMISS_KEY) || localStorage.getItem(DISMISS_KEY) || 0);
  if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return false;
  return true;
};

const PushNotificationPrompt = () => {
  const [visible, setVisible] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [hint, setHint] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      if (shouldShowPrompt()) setVisible(true);
    }, 2500);

    const handleAuth = () => {
      if (shouldShowPrompt()) setVisible(true);
    };

    window.addEventListener('codex-authenticated', handleAuth);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('codex-authenticated', handleAuth);
    };
  }, []);

  const dismiss = () => {
    const now = String(Date.now());
    sessionStorage.setItem(DISMISS_KEY, now);
    localStorage.setItem(DISMISS_KEY, now);
    setVisible(false);
  };

  const handleEnable = async () => {
    setEnabling(true);
    setHint('');
    try {
      const result = await registerPushNotifications({ requestPermission: true, notifyOnSuccess: true });
      if (result.success) {
        setVisible(false);
        return;
      }
      if (result.reason === 'awaiting_user_interaction') {
        setHint('Browser permission dialog will appear on your next click.');
        return;
      }
      if (result.reason === 'permission_denied') {
        setHint('Notifications are blocked. Enable them in browser site settings.');
        return;
      }
      if (result.reason === 'not_configured') {
        setHint('Push is not configured on the server yet.');
        return;
      }
      setHint('Could not enable notifications on this device.');
    } finally {
      setEnabling(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="push-notification-prompt" role="dialog" aria-label="Enable notifications">
      <div className="push-notification-prompt-inner">
        <button type="button" className="push-notification-prompt-close" onClick={dismiss} aria-label="Dismiss">
          <FaTimes />
        </button>
        <div className="push-notification-prompt-icon" aria-hidden="true">
          <FaBell />
        </div>
        <div className="push-notification-prompt-copy">
          <strong>Stay updated with alerts</strong>
          <p>Get instant notifications for bookings, meeting rooms, payments, and workspace invites.</p>
        </div>
        <div className="push-notification-prompt-actions">
          <button type="button" className="push-notification-prompt-enable" onClick={handleEnable} disabled={enabling}>
            {enabling ? 'Enabling...' : 'Enable notifications'}
          </button>
          <button type="button" className="push-notification-prompt-later" onClick={dismiss}>
            Not now
          </button>
        </div>
        {hint && <p className="push-notification-prompt-hint">{hint}</p>}
      </div>
    </div>
  );
};

export default PushNotificationPrompt;
